import { useEffect } from "react";
import { Languages, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui";
import { useFormat } from "@/i18n/format";

/**
 * The consent step before a translation runs.
 *
 * Translating spends the workspace's own model credit, and it used to start
 * from a single click on a dropdown option — an easy misclick with a real
 * bill. This states what will happen, from which language to which, and
 * waits for a yes.
 */
export function TranslateConfirmDialog({
  sourceLanguage,
  targetLanguage,
  onConfirm,
  onClose,
}: {
  /** Detected transcript language; null when detection was unsure. */
  sourceLanguage: string | null;
  targetLanguage: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(["recording", "common"]);
  const f = useFormat();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("translate.dialogLabel")}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-5 shadow-[var(--shadow-md)]">
        <div className="flex items-start justify-between gap-4">
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-text">
            <Languages size={16} className="text-accent" />
            {t("translate.title", { language: f.languageName(targetLanguage) })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-surface-hover hover:text-text"
            aria-label={t("common:actions.close")}
          >
            <X size={16} />
          </button>
        </div>

        <p className="mt-3 text-[13.5px] leading-relaxed text-text-muted">
          {sourceLanguage
            ? t("translate.bodyFrom", {
                source: f.languageName(sourceLanguage),
                target: f.languageName(targetLanguage),
              })
            : t("translate.bodyOriginal", { target: f.languageName(targetLanguage) })}
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            <Languages size={13} />
            {t("translate.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
